import React from "react";

/**
 * The help link (SUPPORT_EMAIL, 2026-10-14 addendum). Its mailto: carries the
 * support address and a fixed subject naming the program, and NOTHING else:
 * never the user's email, a token, a cookie or anything from the page's URL,
 * because a mailto: lands in someone's mail client with whatever it holds.
 * With no support address configured there is no link at all, never a guess.
 */

export interface SupportConfig {
  supportEmail: string | null;
  programShort: string;
}

export function supportMailto(c: SupportConfig): string | null {
  if (!c.supportEmail) return null;
  return `mailto:${c.supportEmail}?subject=${encodeURIComponent(`Medical Admin help — ${c.programShort}`)}`;
}

export function SupportLink({ c }: { c: SupportConfig }): React.ReactElement | null {
  const href = supportMailto(c);
  if (!href) return null;
  return (
    <a className="font-medium text-program underline break-all" href={href}>
      {c.supportEmail}
    </a>
  );
}

/** On every screen, signed in or not. Renders nothing without an address. */
export function SupportFooter({ c }: { c: SupportConfig | null }): React.ReactElement | null {
  if (!c?.supportEmail) return null;
  return (
    <footer className="mx-auto max-w-6xl px-4 pb-8 text-center text-sm text-slate-600 print:hidden">
      Problem signing in or using the app? Email <SupportLink c={c} />
    </footer>
  );
}

/**
 * The "can't sign in with this link" screen: locked, invalidated, or a link
 * that expired or was already used. With a support address, the way out is in
 * the message itself; without one, the server's own message stands.
 */
export function DeadLink({
  code,
  message,
  c,
}: {
  code: string | undefined;
  message: string;
  c: SupportConfig | null;
}): React.ReactElement {
  const link = c?.supportEmail ? <SupportLink c={c} /> : null;
  let body: React.ReactNode = message;
  if (link && code === "locked") body = <>Your account is locked. Email {link} to have it unlocked.</>;
  else if (link && code === "invalidated")
    body = <>Your sign-in has been stopped after too many wrong PINs. Email {link} to have it restored.</>;
  else if (link) body = <>{message} If this keeps happening, email {link}.</>;
  const relink = code !== "locked" && code !== "invalidated";
  return (
    <div className="section-card" role="alert">
      <h2 className="text-lg font-semibold mb-2">Can't sign in with this link</h2>
      <p className="text-sm text-slate-700 mb-4">{body}</p>
      <a className={relink ? "btn-primary" : "btn-secondary"} href="/">
        Request a new link
      </a>
    </div>
  );
}
